/**
 * Stale-build detection (roc-s9fj).
 *
 * Azure SWA replaces the site wholesale on every deploy: each build emits new
 * Vite content hashes and the previous build's assets are deleted. A tab that
 * was already open still holds the old module graph, so the next lazy
 * `import()` it performs — a router route, or a `defineAsyncComponent` like the
 * waypoint editor's 3D preview — requests a chunk URL that no longer exists and
 * fails with:
 *
 *   TypeError: Failed to fetch dynamically imported module: .../assets/X-<hash>.js
 *
 * Nothing is misconfigured when this happens. index.html is served `no-cache`,
 * and `staticwebapp.config.json` deliberately excludes `/assets/*` from the
 * navigation fallback so a missing chunk 404s instead of being answered with
 * HTML — which is what makes the failure a clean promise rejection rather than
 * a baffling MIME-type error. The gap is purely that the tab had no way to
 * learn its build had been replaced.
 *
 * This module is imported by main.ts and must stay dependency-free: it runs
 * before the runtime config is installed, so it cannot pull in anything that
 * reads config at module scope (see the contract in main.ts). It only records
 * that the running build is stale and announces it on window;
 * composables/useStaleBuildNotice.ts owns the operator-facing half.
 */

/** Why we believe this tab is running a build the server no longer serves. */
export type StaleBuildReason =
  /** A lazy import 404'd — the operator's action is already blocked. */
  | 'chunk-load-failed'
  /** The polled index.html now names a different entry chunk. */
  | 'entry-changed';

/** Fired on `window` when the running build is first found to be stale. */
export const STALE_BUILD_EVENT = 'roc:stale-build';

/**
 * The entry chunk this tab booted from, read from the DOM rather than from
 * `import.meta.url`: chunking decides which file this module lands in, but the
 * document's module script is always the entry, which is what /index.html has
 * to be compared against. In dev this is `/src/main.ts` and never rotates.
 */
const bootEntry: string | null =
  document
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute('src') ?? null;

let reason: StaleBuildReason | null = null;

/** The entry chunk URL this tab booted from, or null if the document had no
 *  module script (should not happen; the caller decides how loudly to care). */
export function bootEntryUrl(): string | null {
  return bootEntry;
}

/** Why this tab is stale, or null while it is still current. */
export function staleBuildReason(): StaleBuildReason | null {
  return reason;
}

/**
 * The entry chunk /index.html currently names.
 *
 * `no-store` because a cached copy is exactly the thing we are trying to see
 * past. The regex is deliberately narrow — Vite emits a single
 * `<script type="module" crossorigin src="/assets/index-<hash>.js">` and no
 * other module script — and a miss throws rather than guessing, since silently
 * treating an unparseable document as "no change" would disable the check for
 * good without saying so.
 */
export async function readServerEntry(): Promise<string> {
  // Bounded (roc-pvnv). Callers read a throw as "cannot answer" and a hang as
  // nothing at all — the poll's tick would simply never return, and the check
  // would be quietly off for the rest of the session.
  const res = await fetch('/index.html', {
    cache: 'no-store',
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) throw new Error(`GET /index.html returned ${res.status}`);
  const html = await res.text();
  const match = html.match(/<script[^>]*\stype="module"[^>]*\ssrc="([^"]+)"/);
  if (!match) {
    throw new Error('no <script type="module"> found in /index.html');
  }
  return match[1];
}

/**
 * Ask the server whether this tab's build is the one it still serves.
 *
 * Returns null when the question cannot be answered — no module script in the
 * document, or the probe failed — so callers can tell "current" apart from
 * "unknown" instead of reading a failed check as good news. Callers that also
 * want the operator told should `markStaleBuild('entry-changed')` on true; this
 * function only reports, because the boot-failure path in main.ts needs the
 * answer before anything is listening for the event.
 */
export async function isRunningStaleBuild(): Promise<boolean | null> {
  const booted = bootEntry;
  if (booted === null) return null;
  try {
    return (await readServerEntry()) !== booted;
  } catch (err) {
    console.warn('[stale-build] version check failed', err);
    return null;
  }
}

/**
 * Record that the running build is stale and announce it.
 *
 * Every call announces. Deduping lives with whoever knows what a duplicate
 * means: the burst of preload failures from one navigation is collapsed by the
 * listener below, and the poll's repeated sightings of the same new build are
 * collapsed by useStaleBuildNotice. Deduping here on `reason` alone would eat
 * the case that matters most — a second deploy landing while the notice from
 * the first is still on screen.
 */
export function markStaleBuild(next: StaleBuildReason): void {
  reason = next;
  window.dispatchEvent(new CustomEvent<StaleBuildReason>(STALE_BUILD_EVENT, { detail: next }));
}

// Vite wraps every dynamic import in __vitePreload, which dispatches a
// cancelable `vite:preloadError` when either the dependency preload or the
// import itself rejects, then rethrows unless the event was default-prevented.
//
// We deliberately do NOT preventDefault. Cancelling makes __vitePreload resolve
// to undefined, which turns a precise "failed to fetch module" into Vue
// complaining about an invalid async component result. Letting it throw keeps
// the real error in the console and the failing component in its error state;
// this listener only adds the part that was missing — telling the operator why,
// and offering the reload that fixes it.
window.addEventListener('vite:preloadError', (event) => {
  const payload = (event as Event & { payload?: unknown }).payload;
  console.error('[stale-build] dynamic import failed — this build is likely gone', payload);
  // One navigation can fail several preloads; the operator needs telling once.
  if (reason === 'chunk-load-failed') return;
  markStaleBuild('chunk-load-failed');
});
